import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import type { Batch } from "../../api/batches";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { StatusChip } from "./StatusChip";

export function batchModeLabel(b: Batch): string {
  if (b.mode === "auto") return "Auto";
  return b.type === "invoice_audit" ? "Invoice audit" : b.type === "run_skill" ? "Run skill" : (b.type ?? "Explicit");
}

/** Terminal share of the batch, 0..100 (succeeded + failed + cancelled). */
export function progressPct(b: Batch): number {
  if (!b.total) return 0;
  return Math.round(((b.succeeded + b.failed + b.cancelled) / b.total) * 100);
}

export function BatchRow({ batch, onOpen }: { batch: Batch; onOpen: (id: string) => void }) {
  const pct = progressPct(batch);
  return (
    <WorkspaceCard
      data-testid="batch-row"
      data-batch-id={batch.id}
      onClick={() => onOpen(batch.id)}
      sx={{ cursor: "pointer" }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {batch.name || "Untitled batch"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {batchModeLabel(batch)} · {new Date(batch.created_at).toLocaleString()}
          </Typography>
        </Stack>
        <StatusChip status={batch.status} />
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={batch.failed > 0 ? "warning" : "primary"}
          aria-label="batch progress"
          data-testid="batch-progress"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {batch.succeeded} done · {batch.failed} failed · {batch.held} held · {batch.total} total
        </Typography>
      </Box>
    </WorkspaceCard>
  );
}
